import classNames from 'classnames';
import { useContext, useMemo } from 'react';
import {
  formatTaskDateTime,
  formatTaskDateTimeWithLocalTime,
} from '@/modules/common/helpers/taskDateTime.ts';
import { priorityColors } from '@/modules/admin-dashboard/components/modals/TaskModal.tsx';
import TaskResultInfoModalWithLoading from '@/modules/admin-dashboard/components/modals/TaskResultInfoModalWithLoading.tsx';
import { DisclosureListItem, ModalProviderContext, TextWithShortLinks } from 'ui';
import {
  TaskListWithRelationsResponse,
  TaskListWithRelationsSummaryResponse,
  TaskResultSchema,
  TaskResultSummaryResponse,
  User,
} from 'compliance-api';

interface TaskHistoryItemProps {
  task: TaskListWithRelationsResponse | TaskListWithRelationsSummaryResponse;
}

type TaskHistoryResult = TaskResultSchema | TaskResultSummaryResponse;

const TaskHistoryItem = ({ task }: TaskHistoryItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const taskResultsItems = useMemo(
    () =>
      (task.task_results || []).reduce(
        (acc: Array<{ user: User; result: TaskHistoryResult }>, result) => {
          const user = (task.users || []).find(item => item.id === result.completed_by_user_id);

          if (user) {
            acc.push({ user, result });
          }

          return acc;
        },
        [],
      ),
    [task.task_results, task.users],
  );

  const latestResult = taskResultsItems[0]?.result;

  const bottomCustomContent = !!taskResultsItems.length && (
    <div className="w-full">
      <div className="border-b ml-[-24px] mr-[-24px]" />
      {taskResultsItems.map(({ user, result }) => {
        const dueDate = 'due_date' in result ? result.due_date : null;
        const completedAtDateTime = formatTaskDateTimeWithLocalTime(result.completion_date);
        const dueDateTime = formatTaskDateTimeWithLocalTime(dueDate);
        const handleOpenResult = () => {
          openModal(TaskResultInfoModalWithLoading, {
            task,
            user,
            resultId: result.id,
          });
        };

        return (
          <div
            key={result.id}
            className="flex items-center justify-between [&:not(:last-child)]:border-b py-4 cursor-pointer group"
            onClick={handleOpenResult}
          >
            <div className="flex items-center gap-4">
              <span className="w-[150px] group-hover:underline">
                {user.first_name} {user.last_name}
              </span>
              <div className="flex flex-col gap-2 w-[140px]">
                <div className="text-[12px] text-gray-500">ID</div>
                <div className="capitalize">{result.reference_name || '---'}</div>
              </div>
              <div className="flex flex-col gap-2 w-[100px]">
                <div className="text-[12px] text-gray-500">Result</div>
                <div className="capitalize">{result.result}</div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="text-[12px] text-gray-500">Completed at</div>
                <div>
                  <div>{completedAtDateTime.taskDateTime}</div>
                  {completedAtDateTime.localDateTime && (
                    <div className="text-xs text-gray-500">
                      local time: {completedAtDateTime.localDateTime}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 w-[180px] ml-4">
                <div className="text-[12px] text-gray-500">Due Date</div>
                <div className="whitespace-nowrap">
                  <div>{dueDateTime.taskDateTime}</div>
                  {dueDateTime.localDateTime && (
                    <div className="text-xs text-gray-500">
                      local time: {dueDateTime.localDateTime}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <DisclosureListItem
      leftMainString={task.name}
      bottomCustomContent={bottomCustomContent}
      contentGridColumns="minmax(auto, 30%) 1fr min-content"
      classes={{
        leftMainString: 'pl-2 sm:pl-0',
        container: 'relative',
        rightContent: 'justify-end !border-none',
      }}
      leftSubduedContentSet={[
        task.description ? <TextWithShortLinks text={task.description} /> : '...',
      ]}
      leftContent={
        <div
          className={classNames(
            'absolute left-0 top-0 w-[11px] h-full sm:rounded-tl-xl sm:rounded-bl-xl',
            priorityColors[task.priority],
          )}
          style={{ clipPath: 'inset(0px 6px 0px 0px)' }}
        />
      }
      rightLeftContent={
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 justify-end items-start">
          <div className="hidden lg:flex flex-col w-[100px] justify-start">
            <span className="text-[12px] text-gray-500">Results</span>
            <span>{taskResultsItems.length}</span>
          </div>
          <div className="flex flex-col justify-end">
            <span className="text-[12px] text-gray-500">Completed date</span>
            <span>{latestResult ? formatTaskDateTime(latestResult.completion_date) : '---'}</span>
          </div>
        </div>
      }
    />
  );
};

export default TaskHistoryItem;
